import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Alert, Button, Group, Stack } from '@mantine/core';
import { Download, X, HelpCircle } from 'lucide-react';
import { t, type Lang } from '../i18n';
import { InstallHelpModal } from './InstallHelpModal';
import { useInstallHelpModal } from '../hooks/useInstallHelpModal';
import {
    shouldShowInstallPrompt,
    dismissInstallPrompt,
    triggerInstallPrompt,
    isIOS
} from '../utils/mobileInstallation';
interface MobileInstallBannerProps {
    lang: Lang;
    onComplete?: () => void;
}

export const MobileInstallBanner = ({ lang, onComplete }: MobileInstallBannerProps) => {
    const [show, setShow] = useState(false);
    const installHelpModal = useInstallHelpModal();

    useEffect(() => {
        if (shouldShowInstallPrompt()) {
            // Delay showing banner to avoid initial layout shift
            const timer = setTimeout(() => {
                setShow(true);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, []);

    const handleInstall = async () => {
        setShow(false);

        if (isIOS()) {
            // For Safari (iOS, macOS, iPadOS), show help modal with instructions
            installHelpModal.open();
        } else {
            // For Android, trigger the native install prompt
            const installed = await triggerInstallPrompt();
            if (installed) {
                dismissInstallPrompt();
            }
            // Close banner and show VideoRoom regardless of outcome
            onComplete?.();
        }
    };

    const handleDismiss = () => {
        setShow(false);
        dismissInstallPrompt();
        onComplete?.();
    };

    const banner = show ? (
        <Alert
            icon={<Download size={16} />}
            title={t(lang, 'install_app_title')}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                borderRadius: 0,
                marginBottom: 0,
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid #e0e0e0',
            }}
        >
            <Stack gap="sm">
                <div style={{ color: 'blue' }}>{t(lang, 'install_app_prompt')}</div>
                <Group justify="flex-end" gap="xs">
                    {!isIOS() && (
                        <Button
                            variant="subtle"
                            size="xs"
                            onClick={installHelpModal.open}
                            leftSection={<HelpCircle size={14} />}
                        >
                            {t(lang, 'help')}
                        </Button>
                    )}
                    <Button
                        variant="default"
                        size="xs"
                        onClick={handleDismiss}
                        rightSection={<X size={14} />}
                    >
                        {t(lang, 'install_app_dismiss')}
                    </Button>
                    <Button size="xs" onClick={handleInstall} color="blue">
                        {t(lang, 'install_app_button')}
                    </Button>
                </Group>
            </Stack>
        </Alert>
    ) : null;

    return (
        <>
            {createPortal(banner, document.body)}
            <InstallHelpModal
                lang={lang}
                opened={installHelpModal.opened}
                onClose={() => {
                    installHelpModal.close();
                    onComplete?.();
                }}
            />
        </>
    );
};
